import { useBreadcrumbs } from '@hooks/useBreadcrumbs';
import {
  updateReportStatusSuccess,
  useClinicalTrialReportsSlice,
} from '@restore/clinicalTrialReports/clinicalTrialReports.slice';
import { useClinicalTrialSlice } from '@restore/clinicalTrial/clinicalTrial.slice';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Table } from '@dynamika-shared/ui/Table/Table';
import { makeReportColumns } from './ClinicalTrialReports.table.config';
import { usePaginationAndSort } from '@restore/helpers';
import { OnSortColumnType } from '@dynamika-shared/ui/Table/Table.types';
import { useMakePagination } from '@restore/paginationAndSort/paginationAndSort.slice';
import { SearchSuffix } from '@dynamika-shared/ui/Input/SearchSuffix';
import { Input } from '@dynamika-shared/ui/Input/Input';
import useDebounceFn from '@shared/hooks/useDebouncedValue';
import { PDFReport as PDFReportModal } from 'src/components/modals/PDFReport/PDFReport';
import {
  ClinicalTrialReport,
  ReportReviewStatus,
  ClinicalTrialReportUpdateStatusRequestAction,
} from '@restore/clinicalTrialReports/clinicalTrialReports.types';
import {
  Procedure,
  ReportProcedureConfirmationModal,
} from '../../../components/modals/ReportProcedureConfirmation/ReportProcedureConfirmation';

type ProcedureData = {
  procedure: Procedure;
  patientCode: string;
  clinicalTrialId: string;
  reportId: string;
  reportName: string;
};
import { FilterButtons } from 'src/components/FilterButtons/FilterButtons';
import { subscribeToActions } from 'src/store-redux/store';

const filtersStatus = {
  new: 'NEW',
  released: 'APPROVED',
  rejected: 'REJECTED',
} as const;

export const ClinicalTrialReports = () => {
  const [searchValue, setSearchValue] = useState('');
  const [reviewStatus, setReviewStatus] = useState<ReportReviewStatus>('NEW');

  const [PDFReportName, setPDFReportName] = useState<string | null>(null);
  const [procedure, setProcedure] = useState<ProcedureData | null>(null);
  const [clinicalTrial] = useClinicalTrialSlice(state => [state.data]);

  useBreadcrumbs(
    [
      ['Clinical Trials', 'clinicalTrials'],
      [clinicalTrial?.name, 'clinicalTrialGeneralInformation'],
      ['Reports', 'clinicalTrialReports'],
    ],
    [clinicalTrial?.id]
  );

  const { sortBy, currentPage, currentPageSize, setSortBy, setCurrentPage } =
    usePaginationAndSort('reports');

  const [
    PDFReportURL,
    PDFReportURLError,
    PDFReportURLLoading,
    reports,
    totalElements,
    loading,
    loadingFile,
    loadingProcedure,
    search,
    setSearch,
    fetchReports,
    getReportFile,
    removeReportFile,
    updateReportStatusRequest,
    clear,
  ] = useClinicalTrialReportsSlice((state, actions) => [
    state.PDFReport?.url,
    state.fileError,
    state.loadingFile,
    state.data.reports,
    state.data.totalElements,
    state.loading || state.loadingDictionaries,
    state.loadingFile,
    false,
    state.search,
    actions.setSearch,
    actions.fetchClinicalTrialReportsRequest,
    actions.fetchReportUrlRequest,
    actions.removeReportUrl,
    actions.updateReportStatusRequest,
    actions.clear,
  ]);


  useEffect(() => {
    return () => {
      clear();
    };
  }, []);

  const onSort: OnSortColumnType = (name, sortState) => {
    if (clinicalTrial) {
      const newSort = sortState ? `${name},${sortState}` : undefined;
      setCurrentPage({ listName: 'reports', newPage: 1 });
      setSortBy({ listName: 'reports', newSortBy: newSort });
    }
  };

  const pagination = useMakePagination('reports', !!totalElements);

  const openPDFReport = useCallback((data: ClinicalTrialReport) => {
    setPDFReportName(data.reportName ?? null);
    return getReportFile({
      clinicalTrialId: data.clinicalTrialId,
      reportId: data.id,
      reportName: data.reportName ?? 'report',
    });
  }, []);

  const columns = useMemo(() => {
    return makeReportColumns({
      action: (data, procedure) => {
        if (procedure === 'pdf') {
          openPDFReport(data);
        }
        if (
          procedure === 'release' ||
          procedure === 'reject' ||
          procedure === 'release_and_email'
        ) {
          setProcedure({
            procedure,
            patientCode: data.patientCode,
            clinicalTrialId: data.clinicalTrialId,
            reportId: data.id,
            reportName: data.reportName ?? 'report',
          });
        }
      },
    });
  }, [openPDFReport]);

  const confirmProcedure = useCallback(
    (procedure: ProcedureData) => {
      const payloadMap: Record<string, ClinicalTrialReportUpdateStatusRequestAction['payload']> = {
        release: {
          clinicalTrialId: procedure.clinicalTrialId,
          reportId: procedure.reportId,
          status: 'APPROVED',
          notification: false,
        },
        reject: {
          clinicalTrialId: procedure.clinicalTrialId,
          reportId: procedure.reportId,
          status: 'REJECTED',
        },
        release_and_email: {
          clinicalTrialId: procedure.clinicalTrialId,
          reportId: procedure.reportId,
          status: 'APPROVED',
          notification: true,
        },
      };

      const payload = payloadMap[procedure.procedure];
      if (payload) {
        updateReportStatusRequest(payload);
      }
      setProcedure(null);
    },
    [updateReportStatusRequest]
  );

  const handleSearch = (value: string) => {
    setSearchValue(value);
    setCurrentPage({ listName: 'reports', newPage: 1 });
    setSearch(value);
  };

  const { run: delayedSearch } = useDebounceFn((value: string) => {
    handleSearch(value);
  }, 1000);

  useEffect(() => {
    return subscribeToActions(updateReportStatusSuccess.type, () => {
      if (clinicalTrial?.id) {
        fetchReports({
          clinicalTrialId: clinicalTrial?.id,
          page: currentPage,
          size: currentPageSize,
          ...(sortBy && { sort: sortBy }),
          reviewStatus,
          search,
        });
      }
    });
  }, [clinicalTrial?.id, currentPage, currentPageSize, sortBy, search, reviewStatus]);

  useEffect(() => {
    if (clinicalTrial?.id) {
      fetchReports({
        clinicalTrialId: clinicalTrial?.id,
        page: currentPage,
        size: currentPageSize,
        ...(sortBy && { sort: sortBy }),
        reviewStatus,
        search,
      });
    }
  }, [
    clinicalTrial?.id,
    currentPage,
    currentPageSize,
    sortBy,
    search,
    reviewStatus,
  ]);

  return (
    <div className='relative'>
      <div className='flex justify-between items-center'>
        <div className='flex w-full items-center justify-between'>
          <FilterButtons
            mode='single'
            selectedFilters='new'
            filters={['new', 'released', 'rejected']}
            toggleSelectedFilter={filterName => {
              const value =
                filtersStatus[filterName as keyof typeof filtersStatus];
              setReviewStatus(value);
            }}
          />
          <div>
            <Input
              value={searchValue}
              onChange={e => {
                const searchVal = e.target.value.trim();
                setSearchValue(searchVal);
                !searchVal ? handleSearch(searchVal) : delayedSearch(searchVal);
              }}
              className='mini:h-[3.2rem] mini:min-w-[24rem] mini:px-1 !mt-0 h-[4.8rem] min-w-[32rem] !rounded-[3.2rem] px-2'
              placeholder='Search by Subject ID'
              suffix={
                <SearchSuffix
                  disabled={!searchValue}
                  action={() => {
                    setSearchValue('');
                    delayedSearch('');
                  }}
                />
              }
              data-testid={'reportsSearch'}
            />
          </div>
        </div>
      </div>
      {loadingFile && (
        <div className='w-full h-full fixed top-0 left-0 z-50 cursor-progress' />
      )}
      <Table
        className='mt-3'
        loading={loading}
        data={reports}
        columns={columns}
        onSort={onSort}
        sortState={sortBy}
        pagination={pagination}
        totalItems={totalElements}
      />
      {procedure && (
        <ReportProcedureConfirmationModal
          procedure={procedure?.procedure}
          patientCode={procedure?.patientCode}
          onCancel={() => {
            setProcedure(null);
          }}
          loading={loadingProcedure}
          onConfirm={() => {
            confirmProcedure(procedure);
          }}
          open={!!procedure}
          cancelText='No'
          okText='Yes'
        />
      )}
      <PDFReportModal
        key={PDFReportURL}
        title={PDFReportName ?? ''}
        error={PDFReportURLError as string}
        isOpen={PDFReportURLLoading || !!PDFReportName}
        reportLoading={PDFReportURLLoading}
        url={PDFReportURL ?? ''}
        onClose={() => {
          removeReportFile();
          setPDFReportName(null);
        }}
      />
    </div>
  );
};
